import type { EKycStep } from "./common";
import type { TIndividualSurveyQuestionnaire } from "./kycIndividualSurveyQuestionnaire";
import type { TInstitutionSurveyQuestionnaire } from "./kycInstitutionSurveyQuestionnaire";
import type { IInstitutionInformation } from "./kycBasicInstitution";
import type { StyleProp, ViewStyle, TextStyle } from "react-native";
import type {ICategoryItem, IOptionItem} from "./component";

// 统一选项格式
export interface IFieldOption {
  title: string;
  value: string;
}

// 基础字段配置
export interface IBaseFieldConfig<T> {
  name: string;
  label: string;
  subLabel?: string;
  placeholder?: string;
  trim?: boolean;
  hideErrorSpace?: boolean;
  style?: StyleProp<ViewStyle & TextStyle>;
  onChangeCallback?: (value: string, formData: T) => void;
  validation?: IValidationRule[];
  tip?: string;
  fieldSequenceNumber?: number;
}

// 输入框字段配置
export interface IInputFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "input";
  valueType?: "text" | "number";
}

// 选择器字段配置
export interface ISelectFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "select";
  options: IFieldOption[];
}

// 日期选择器字段配置
export interface IDatePickerFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "datePicker";
}

// 电话组字段配置
export interface IPhoneGroupFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "phoneGroup";
  phonePrefixField: keyof T;
  phoneField: keyof T;
}

// 百分比字段配置
export interface IPercentageFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "percentage";
  suffix?: string;
}

// 单选(包括选项中带有输入框)字段配置
export interface IRadioFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "radio" | "radioWithInput" | "listedEntity";
  options: IFieldOption[];
  subOptions?: IFieldOption[];
}

// 单选带有选择框字段配置
export interface IRadioWithSelectFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "radioWithSelect";
  options: IFieldOption[];
  subOptions?: IFieldOption[];
}

// 多选(包括选项中带有输入框)字段配置
export interface ICheckboxFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "checkbox" | "checkboxWithInput";
  options: IFieldOption[];
}

// 股东信息字段配置
export interface IStakeholdersFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "shareholders";
}

// 受益人信息字段配置
export interface IBeneficiariesFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "beneficiaries";
}

// 董事字段配置
export interface IDirectorsFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "directors";
}

// 授权签字人字段配置
export interface IAuthorisedSignatoriesFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "authorisedSignatories";
}

// 资金来源字段配置
export interface IFundSourcesFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "fundSources";
}

// 税务居住地字段配置
export interface ITaxResidencesFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "taxResidences";
}

// 多维表单组件配置
export interface ICategoryRadioGroupFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "categoryRadioGroup";
  categoryOptions: ICategoryItem[];
  /** Web 端选项之间的横向间距（px），默认 100 */
  optionGap?: number;
}

// 所有者财富来源字段配置
export interface IWealthSourceOfOwnerFieldConfig<T> extends IBaseFieldConfig<T> {
  type: "wealthSourceOfOwner";
}

// 联合类型，根据 type 字段精确约束其他字段
export type IFieldConfig<T> =
  | IInputFieldConfig<T>
  | ISelectFieldConfig<T>
  | IDatePickerFieldConfig<T>
  | IPhoneGroupFieldConfig<T>
  | IPercentageFieldConfig<T>
  | IRadioFieldConfig<T>
  | ICheckboxFieldConfig<T>
  | IStakeholdersFieldConfig<T>
  | IBeneficiariesFieldConfig<T>
  | IDirectorsFieldConfig<T>
  | IAuthorisedSignatoriesFieldConfig<T>
  | IFundSourcesFieldConfig<T>
  | IRadioWithSelectFieldConfig<T>
  | ITaxResidencesFieldConfig<T>
  | ICategoryRadioGroupFieldConfig<T>
  | IWealthSourceOfOwnerFieldConfig<T>;
// 验证规则类型
export interface IValidationRule {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: RegExp;
  message?: string;
}

export type TValidationRules = Partial<Record<string, IValidationRule[]>>;

export interface INestedFormConfig<T> {
  groups: IFieldGroup[];
  validationRules: TValidationRules;
  initialData: T;
}

export interface IFieldGroup {
  type: "group";
  name: string;
  label: string;
  subLabel?: string;
  fields: IFieldConfig<string | string[]>[];
  horizontalLayout?: boolean; // 表示 fields 是否采用横向布局
}

export type TFieldOptionConfig = {
  index?: number; // Group中的顺序
  groupName?: string;
  horizontalLayout?: boolean; // 表示 fields 是否采用横向布局
};

// 字段或分组联合类型
export type TFieldOrGroup = IFieldConfig<string | string[]> | IFieldGroup;

export interface IKycStakeholderInfo {
  name?: string;
  shareholding?: number | string;
  gender?: string;
  birthdate?: string;
  nationality?: string;
  email?: string;
  phone?: string;
  documentCheckStatus?: string;
  documentFailureReason?: string;
  faceCheckStatus?: string;
  faceFailureReason?: string;
}

export enum IKyc1LevelStatus {
  init = 0, // 未认证
  Auditing = 1, // 1 审核中
  Rejected = 2, // 2 未通过
  Certified = 3, // 3 通过
}

export enum IKyc2LevelStatus {
  Auditing = 4, // 4 审核中
  Rejected = 5, // 5 未通过
  Certified = 6, // 6 通过
}

export enum typeList {
  Institutions, // 0 机构
  Individuals, // 1 个人
}

export type TIndividualQuestionnaireForm = TIndividualSurveyQuestionnaire;

export type TInstitutionQuestionnaireForm = TInstitutionSurveyQuestionnaire;

export type TIndividualQuestionnaireFieldItem = IFieldConfig<TIndividualQuestionnaireForm>;

export type TInstitutionQuestionnaireFieldItem = IFieldConfig<TInstitutionQuestionnaireForm>;

export type TInstitutionInformationForm = IInstitutionInformation;

export type TInstitutionInformationFieldItem = IFieldConfig<TInstitutionInformationForm>;

// KYC 认证 状态
export enum IKycStatusV2 {
  init = 0, // 未认证(没有提交过kyc)
  Auditing = 7, // 审核中
  Certified = 8, // 通过
  Rejected = 9, // 未通过(被拒绝)
}

export type TKYCApiVersion = "V1" | "V2";

// KYC接口post数据格式
export type TKycFormV2 = Partial<{
  customerId: string;
  type: typeList;
  status: IKycStatusV2;
  nationality: string;
  files: string[];
  institution: IInstitutionInformation;
  individualSurveyQuestionnaire: TIndividualSurveyQuestionnaire;
  institutionSurveyQuestionnaire: TInstitutionSurveyQuestionnaire;
  // 前段新增字段,用来记录kyc历史步骤
  individualStep?: EKycStep;
  institutionStep?: EKycStep;
  version: TKYCApiVersion;
}>;

// 只有两个Kyc等级 L0: 未认证 L2: 已认证
export enum IKycLevel {
  Initial = "L0",
  Kyc2 = "L2",
}

// KYC接口get数据格式
export type TKycInfoV2 = Omit<TKycFormV2, "status" | "individualStep" | "institutionStep"> & {
  status: IKyc1LevelStatus | IKyc2LevelStatus; // 后端返回的status是 IKycStatusV2,需要转换为前端需要的status
  level: IKycLevel;
  nationality?: string; //地区选择,个人kyc有
  reviewReason?: string; //拒绝理由,老接口的remark字段
  name?: string;
  email?: string;
  remark?: string;
  companyRegistrationCountry?: string;
  countryCode?: string;
};

// KYC 文档上传相关类型定义
/**
 * KYC 文档表格行数据结构
 */
export interface TKycDocumentItem {
  /** 普通行的文档类型 */
  documentType?: string;
  /** 特殊行的多格文档类型（第一列分为多个格子） */
  multiRowDocumentType?: string[];
  /** 文档名称 */
  document: string;
  /** 替代文档 */
  alternative: string;
  /** 认证要求 */
  certification: string;
  /** 有效期 */
  validity: string;
}

/**
 * KYC Upload Component Props - Web Implementation
 */
export interface TKycUploadWebProps {
  kycType?: "individuals" | "institutions";
  onSubmit?: (files: string[]) => void;
  loading?: boolean;
}

/**
 * 表格行数据接口
 * @description 定义表格每一行的数据结构，支持动态字段
 */
export interface IRowData {
  /** 动态字段，支持任意键值对 */
  [key: string]: any;
}

/**
 * 表格列配置接口
 * @description 定义表格列的显示和行为配置
 */
export interface IColumnConfig {
  /** 列的唯一标识符 */
  key: string;
  /** 列标题，显示在表头 */
  title: string;
  /** 数据索引，对应rowData中的字段名 */
  dataIndex: string;
  /** 是否为必填字段 */
  required?: boolean;
  /** 字段类型，影响输入验证和显示格式 */
  type?:
    | "text"
    | "number"
    | "percentage"
    | "nationality"
    | "date"
    | "select"
    | "amount"
    | "radio"
    | "radioWithInput";
  /** 自定义渲染函数，用于特殊显示需求 */
  render?: (value: any, record: IRowData) => React.ReactNode;
  /** 下拉选择选项（用于 select 类型） */
  options?: IOptionItem[];
  /** 日期格式（用于 date 类型，默认 "YYYY-MM-DD"） */
  dateFormat?: string;
  /** 输入框占位符文本（用于 radioWithInput 等类型） */
  placeholder?: string;
  /** 分组标题（用于在字段上方显示分组信息，带必填标识） */
  groupTitle?: string;
  /** 互斥字段的 dataIndex 数组（当此字段变化时，会清空互斥字段） */
  mutuallyExclusiveWith?: string[];
  /** 互斥字段组（用于验证至少填写一个） */
  mutuallyExclusiveGroup?: string;
}

/**
 * 组件属性接口
 * @description 定义EditableTable组件的所有属性
 */
export interface IEditableTableProps {
  /** 表格数据，受控模式 */
  value: IRowData[];
  /** 数据变化回调函数 */
  onChange?: (data: IRowData[]) => void;
  /** 列配置数组 */
  columns: IColumnConfig[];
  /** 表格标题 */
  title?: string;
  /** 添加按钮文本 */
  addButtonText?: string;
  /** 最小行数，用于保证表格基本结构 */
  minRows?: number;
  /** 最大行数，限制数据量 */
  maxRows?: number;
  /** 编辑弹窗底部描述文案 */
  descriptionText?: string;
}
